// src/pages/CreatePostPage.js

import React, { useState, useEffect, useRef } from 'react';
import { api } from '../config';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import imageCompression from 'browser-image-compression';
import './CreatePostPage.css';

// Utility to check JWT expiry (same as MyProfilePage)
function isTokenExpired(token) {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (!payload.exp) return true;
    return Date.now() >= payload.exp * 1000;
  } catch {
    return true;
  }
}

const tagOptions = [
  "tourism",
  "hidden gems",
  "local cuisine",
  "adventure sports",
  "stargazing",
  "water activities",
  "wildlife safari",
  "theme parks",
  "treks & hikes",
  "wellness & retreats",
  "beaches",
  "mountains",
  "historic monuments",
  "museums",
  "scenic views",
  "nightlife",
  "festivals",
  "photographic spots",
  "family travel",
  "solo travel",
  "group travel",
  "camping",
  "art & architecture",
  "wine & brewery tours",
  "shopping destination",
  "weekend getaway",
  "staycation",
  "cultural immersion",
  "desert safari",
  "budget travel"
];

const CreatePostPage = () => {

  const location = useLocation();
  const {
    isEditing = false,
    postId = null,
    caption: initialCaption = '',
    location: initialLocation = '',
    tags: initialTags = [],
    postType: initialPostType = 'photo',
    images: initialImages = [],
    video: initialVideo = null,
  } = location.state || {};
  
  
  const { isLoggedIn, accessToken } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [images, setImages] = useState([]);
  const [caption, setCaption] = useState(initialCaption);
  const [locationText, setLocationText] = useState(initialLocation);
  const [previewUrls, setPreviewUrls] = useState(() => {
  if (location.state?.isEditing && Array.isArray(location.state?.images)) {
    // images from MyProfilePage are already full URLs
    return location.state.images;
  }
  return [];
});

  const [error, setError] = useState('');
  const [loading] = useState(false);
  const [hasUsername, setHasUsername] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const latestLocationQueryRef = useRef("");
  const locationDebounceRef = useRef(null);
  const locationAbortRef = useRef(null);
  const [selectedTags, setSelectedTags] = useState(initialTags);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [postType, setPostType] = useState(initialPostType || 'photo');
  const [video, setVideo] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [videoError, setVideoError] = useState('');
  const [deleteVideo, setDeleteVideo] = useState(false);
  const [keepVideo, setKeepVideo] = useState('');
  const [gallery, setGallery] = useState([]);

useEffect(() => {
  if (!isLoggedIn || isTokenExpired(accessToken)) {
    // remember where the user came from
    navigate('/login', { state: { from: location.pathname }, replace: true });
  }
}, [isLoggedIn, accessToken, navigate, location.pathname]);

  useEffect(() => {
    const checkUsername = async () => {
      try {
        const res = await fetch(api.url('/api/roam-share-posts/my-profile'), {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
  
        if (res.ok) {
          const data = await res.json();
          setHasUsername(!!data.username); // true if username exists
        } else {
          setHasUsername(false);
        }
      } catch  {
        
        setHasUsername(false);
      } finally {
        setInitialLoading(false);
      }
    };
  
    checkUsername();
  }, [accessToken]);
  
  useEffect(() => {
    if (!isEditing) return;
  
    setPostType(initialPostType);
  
    if (initialPostType === 'photo') {
  // No File objects yet
  setImages([]);

  // Build gallery from current DB images (full URLs)
  const items = (initialImages || []).map((imgPath) => {
    const filename = imgPath.split('/').pop();   // used for keep_images
    const previewUrl = imgPath;                  // already a full URL
    return { kind: 'existing', previewUrl, filename };
  });

  setGallery(items);
  setPreviewUrls(items.map(i => i.previewUrl));
  setCurrentImageIndex(0);
} else if (initialPostType === 'video' && initialVideo) {
  const vp = initialVideo; // already full URL from backend
  
  setVideoPreview(vp);
  const fname = initialVideo.split('/').pop(); // filename for keep_video
  setKeepVideo(fname || '');
}

  }, [isEditing, initialPostType, initialImages, initialVideo]);
  useEffect(() => {
  return () => {
    if (locationDebounceRef.current) clearTimeout(locationDebounceRef.current);
    if (locationAbortRef.current) {
      try { locationAbortRef.current.abort(); } catch {}
    }
  };
}, []);

  const handleImageChange = async (e) => {
    if (postType === 'video') return;
    const files = Array.from(e.target.files).filter(file => file.type.startsWith('image/'));
  
    const compressedImages = await Promise.all(
      files.map(async (file) => {
        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 1200,
          useWebWorker: true,
          initialQuality: 0.85,
        };
        try {
          const compressed = await imageCompression(file, options);
          return compressed;
        } catch  {
          
          return file; // fallback
        }
      })
    );
  
    const newPreviews = compressedImages.map((img) => URL.createObjectURL(img));
  
    setImages(prev => [...prev, ...compressedImages]);
    setPreviewUrls(prev => [...prev, ...newPreviews]);
    setGallery(prev => [
      ...prev,
      ...compressedImages.map((file, i) => ({
        kind: 'new',
        file,
        previewUrl: newPreviews[i]
      }))
    ]);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
  
    if (postType === 'photo' && images.length === 0 && !isEditing) {
      setError('At least one photo is required to share a post');
      return;
    }
  
    if (postType === 'video' && !video && !videoPreview) {
      setVideoError('Video is required to share a video post');
      return;
    }
  
    if (!locationText.trim()) {
      setError('Location is required to share a post');
      return;
    }
    if (selectedTags.length === 0) {
      setError('Please select at least one tag before posting.');
      return;
    }
    const formData = new FormData();
    formData.append('caption', caption);
    formData.append('location', locationText);
    formData.append('tags', JSON.stringify(selectedTags));
    formData.append('post_type', postType); 
    formData.append('delete_video', deleteVideo ? 'true' : 'false'); // new flag
    formData.append('keep_video', keepVideo || '');


    if (postType === 'photo') {
      // Existing filenames that user kept (still in gallery)
      const keep = gallery
        .filter(it => it.kind === 'existing')
        .map(it => it.filename);
    
      formData.append('keep_images', JSON.stringify(keep));
    
      // Attach only new files
      gallery
        .filter(it => it.kind === 'new')
        .forEach(it => formData.append('images', it.file));
    }
    else if (postType === 'video') {
      if (video) {
        // New video selected
        formData.append('video', video);
        formData.set('delete_video', 'false');
        formData.set('keep_video', '');
      } else if (deleteVideo) {
        // Delete existing video
        formData.set('delete_video', 'true');
        formData.set('keep_video', '');
      } else if (keepVideo) {
        // Keep existing video
        formData.set('keep_video', keepVideo); // just filename
        formData.set('delete_video', 'false');
      }
    }
    
    

    try {
      let response;
      if (isEditing) {
        
        response = await fetch(api.url(`/api/roam-share-posts/update/${postId}`), {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          body: formData,
        });
      } else {
       
        response = await fetch(api.url('/api/roam-share-posts'), {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          body: formData,
        });
      }
  
     const data = await response.json().catch(() => ({}));

if (!response.ok) {
  throw new Error(data?.error || 'Failed to post');
}

navigate('/roam-share');
} catch (err) {
  setError(`Failed to post: ${err?.message || 'Unknown error'}`);
}
  };
  
  const removeImage = (index) => {
    setGallery(prev => {
      const next = [...prev];
  
      // Revoke blob URL for newly added files to avoid memory leaks
      const toRemove = next[index];
      if (toRemove?.kind === 'new' && toRemove.previewUrl?.startsWith('blob:')) {
        try { URL.revokeObjectURL(toRemove.previewUrl); } catch {}
      }
  
      next.splice(index, 1);
  
      // Sync other states from next
      setImages(next.filter(it => it.kind === 'new').map(it => it.file));
      setPreviewUrls(next.map(it => it.previewUrl));
      setCurrentImageIndex(i => Math.max(0, Math.min(i, Math.max(0, next.length - 1))));
  
      return next;
    });
  };
  

  const handleRemoveVideo = () => {
    
    setDeleteVideo(true);   // mark for deletion
    setKeepVideo('');       // don't keep old video
    setVideo(null);         // clear video file
    setVideoPreview(null);  // clear preview
  };
  
  
  return (
    <div className="create-post-page">
      <div className="header">
            <div className="brand" onClick={() => navigate("/")}>
            <img src="/images/customroam-logo.png" alt="Logo" className="logo-img" />
            <span className="brand-text">CUSTOM ROAM</span>
             </div>
              {/* Hamburger menu button */}
              <button className="header-menu-btn" onClick={() => setMenuOpen(!menuOpen)}>
                ☰
               </button>
             <div className={`nav-links ${menuOpen ? 'open' : ''}`}>
                    <span className="nav-link" onClick={() => navigate('/')}>Home</span>
                    <span className="nav-link" onClick={() => navigate('/roam-share')}>Roam Share</span>
                    <span className="nav-link" onClick={() => navigate('/my-itineraries')}>My Itineraries</span>
                    <span className="notification-icon" onClick={() => navigate('/notifications')}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                       height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                     className="bell-svg"
                    >
                        <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"></path>
                         <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                    </svg>
                 </span>
                    <span className="nav-link" onClick={() => navigate('/profile')}>Profile</span>
                </div>
            </div>

      <div className="create-post-container">
      <div className="create-post-header">
  {/* First Row: Back + Heading */}
  <div className="header-row">
    <button onClick={() => navigate('/profile')} className="back-button">
      ← Back to Profile
    </button>
    <h2 className="create-heading">Create New Post</h2>
  </div>

  {/* Second Row: Post Type Selector BELOW */}
  <div className="post-type-row">
    <div className="post-type-selector">
      <label>
        <input
          type="radio"
          name="postType"
          value="photo"
          checked={postType === 'photo'}
          onChange={() => {
            setPostType('photo');
            setVideo(null);
            setVideoPreview(null);
          }}
        />
        📷 Photos
      </label>
      <label>
        <input
          type="radio"
          name="postType"
          value="video"
          checked={postType === 'video'}
          onChange={() => {
            setPostType('video');
            setImages([]);
            setPreviewUrls([]);
            setGallery([]);
          }}
        />
        🎥 Video
      </label>
    </div>
  </div>
</div>

        {initialLoading ? null : (
  hasUsername ? (
    <form onSubmit={handleSubmit} className="create-post-form">
      {postType === 'photo' && (
  <div className="image-section-container">
    {/* ✅ Left Side: Preview Box */}
    <div className="image-preview-box">
      {previewUrls.length > 0 ? (
        <div className="multi-image-preview-container">
          <div className="carousel-container">
          <button
            type="button"                              
            className="carousel-arrow left"
            onClick={() =>
            setCurrentImageIndex(prev =>
           prev > 0 ? prev - 1 : previewUrls.length - 1
          )
        }
         >
            ←
         </button>

            <div className="carousel-image-wrapper">
              <img
                src={previewUrls[currentImageIndex]}
                alt={`Preview ${currentImageIndex}`}
                className="preview-image"
              />
              <button
                type="button" 
                className="delete-image-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  removeImage(currentImageIndex);
                  if (currentImageIndex >= previewUrls.length - 1) {
                    setCurrentImageIndex(Math.max(0, currentImageIndex - 1));
                  }
                }}
              >
                ✕
              </button>
            </div>

            <button
              type="button"                              
              className="carousel-arrow right"
              onClick={() =>
               setCurrentImageIndex(prev => (prev + 1) % previewUrls.length)
             }
           >
                →
            </button>

          </div>
        </div>
      ) : (
        <span className="placeholder-text">No images selected</span>
      )}
    </div>

    {/* ✅ Right Side: Upload Box */}
    <div
      className="image-upload-box"
      onClick={() => document.getElementById('fileInput').click()}
      onDragOver={(e) => e.preventDefault()}
      onDrop={async (e) => {
        if (postType === 'video') return; 
        e.preventDefault();
      
        const droppedFiles = Array.from(e.dataTransfer.files).filter((file) =>
          file.type.startsWith('image/')
        );
      
        const compressed = await Promise.all(
          droppedFiles.map(async (file) => {
            const options = {
              maxSizeMB: 1,
              maxWidthOrHeight: 1200,
              useWebWorker: true,
              initialQuality: 0.85,
            };
            try {
              const result = await imageCompression(file, options);
              console.log("Original size:", (file.size / 1024 / 1024).toFixed(2), "MB");
              console.log("Compressed size:", (result.size / 1024 / 1024).toFixed(2), "MB");
              return result;
            } catch (err) {
              console.error("Compression failed for", file.name);
              return file;
            }
          })
        );
      
        const newPreviews = compressed.map((img) => URL.createObjectURL(img));
      
        setImages((prev) => [...prev, ...compressed]);
        setPreviewUrls((prev) => [...prev, ...newPreviews]);
        setGallery(prev => [
          ...prev,
          ...compressed.map((file, i) => ({
            kind: 'new',
            file,
            previewUrl: newPreviews[i]
          }))
        ]);
      }}
      
    >
      <span className="upload-plus">＋</span>
      <span className="upload-text">Add More Photos</span>
      <input
        id="fileInput"
        type="file"
        accept="image/*"
        multiple
        onChange={handleImageChange}
        style={{ display: 'none' }}
      />
    </div>
  </div>
)}

{postType === 'video' && (
  <div className="video-upload-section">
  <div
  className={`video-preview-box ${isEditing ? 'no-pointer' : ''}`}
  onClick={!isEditing ? () => document.getElementById('videoInput').click() : undefined}
  onDragOver={!isEditing ? (e) => e.preventDefault() : undefined}
  onDrop={
    !isEditing
      ? (e) => {
          e.preventDefault();
          const file = e.dataTransfer.files?.[0];
          if (file && file.type.startsWith('video/')) {
            setVideo(file);
            setVideoPreview(URL.createObjectURL(file));
            setVideoError('');
            setDeleteVideo(false);
            setKeepVideo('');
          }
        }
      : undefined
  }
>
{videoPreview ? (
  <>
    <video controls width="100%" src={videoPreview}></video>
    {!isEditing && (
      <button
        type="button"
        className="delete-video-btn"
        onClick={(e) => {
          e.stopPropagation();
          handleRemoveVideo();
        }}
      >
        ✕
      </button>
    )}
  </>
) : (
  <span className="placeholder-text">Click or drag a video to upload</span>
)}
    </div>

    {/* hidden input the box clicks */}
    <input
      id="videoInput"
      type="file"
      accept="video/*"
      style={{ display: 'none' }}
      onChange={(e) => {
        const file = e.target.files[0];
        if (file) {
          const videoElement = document.createElement('video');
          videoElement.preload = 'metadata';
          videoElement.onloadedmetadata = () => {
            window.URL.revokeObjectURL(videoElement.src);
      
            const duration = videoElement.duration;
            if (duration > 180) { // 180 seconds = 3 minutes
              setVideo(null);
              setVideoPreview('');
              setVideoError('Video must be less than or equal to 3 minutes.');
              return;
            }
      
            setVideo(file);
            setVideoPreview(URL.createObjectURL(file));
            setVideoError('');
          };
          videoElement.src = URL.createObjectURL(file);
        }
      }}
      
    />

    {videoError && <p className="error-message">{videoError}</p>}
  </div>
)}


      <textarea
        placeholder="Write a caption..."
        value={caption}
        onChange={(e) => setCaption(e.target.value)}
        className="caption-input"
        />

<div style={{ position: 'relative' }}>
  <input
    type="text"
    placeholder="Add location ..."
    value={locationText}
    onChange={(e) => {
  const inputValue = e.target.value;
  const q = inputValue.trim();

  setLocationText(inputValue);

  // Always track latest typed value
  latestLocationQueryRef.current = q;

  // Kill any pending debounce timer
  if (locationDebounceRef.current) {
    clearTimeout(locationDebounceRef.current);
    locationDebounceRef.current = null;
  }

  // Abort any in-flight request
  if (locationAbortRef.current) {
    try { locationAbortRef.current.abort(); } catch {}
    locationAbortRef.current = null;
  }

  // If cleared or too short -> hide dropdown immediately
  if (!q || q.length < 3) {
    setSuggestions([]);
    return;
  }

  // Debounce to avoid spamming API + prevents weird “delete then type” issues
  locationDebounceRef.current = setTimeout(async () => {
    const controller = new AbortController();
    locationAbortRef.current = controller;

    try {
      const res = await fetch(
        api.url(`/api/places/google-places-autocomplete-any?query=${encodeURIComponent(q)}`),
        {
          signal: controller.signal,
          headers: {
            // ✅ IMPORTANT: send token so optionalAuth + limiter treat user correctly
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      // If backend throttles, don't keep old dropdown
      if (!res.ok) {
        setSuggestions([]);
        return;
      }

      const data = await res.json();

      // Ignore stale responses
      if (latestLocationQueryRef.current !== q) return;

      setSuggestions(Array.isArray(data.suggestions) ? data.suggestions : []);
    } catch (err) {
      // Abort is expected during rapid typing
      if (err?.name !== "AbortError") {
        console.error("Autocomplete error:", err);
      }
      setSuggestions([]);
    } finally {
      locationAbortRef.current = null;
    }
  }, 250);
}}

    className="location-input"
  />

  {suggestions.length > 0 && (
    <ul className="autocomplete-dropdown">
      {suggestions.map((sugg, index) => (
        <li
          key={index}
          onClick={() => {
  setLocationText(sugg.description);
  latestLocationQueryRef.current = sugg.description; 
  setSuggestions([]);
}}

        >
          {sugg.description}
        </li>
      ))}
    </ul>
  )}
</div>
<div className="tag-selector">
  <label>Select Tags:</label>
  <div className="tag-chip-container">
    {tagOptions.map(tag => (
      <div
        key={tag}
        className={`tag-chip ${selectedTags.includes(tag) ? 'selected' : ''}`}
        onClick={() => {
          setSelectedTags(prev =>
            prev.includes(tag)
              ? prev.filter(t => t !== tag)
              : [...prev, tag]
          );
        }}
      >
        {tag}
      </div>
    ))}
  </div>
</div>
     {error && <p className="error-message">{error}</p>}

      <button type="submit" className="share-button" disabled={loading}>
        {loading ? 'Sharing...' : 'Share Post'}
      </button>
    </form>
  ) : (
    <div className="no-username-warning">
      <p>
        🚫 Please set up your username in{' '}
        <span
          onClick={() => navigate('/profile')}
          style={{ color: '#007bff', textDecoration: 'underline', cursor: 'pointer' }}
        >
          Profile Page
        </span>{' '}
        to create a Roam Share post.
      </p>
    </div>
  )
)}

      </div>
    </div>
  );
};

export default CreatePostPage;
